import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";

const PAGE_SIZE = 10;

const JobApplicationsPage = () => {
  const { user } = useContext(UserContext);
  const [applications, setApplications] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Modals
  const [rejectModal, setRejectModal] = useState({
    open: false,
    app: null,
    reason: "",
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, app: null });

  const fetchApplications = async (next = false) => {
    if (!user?.id || loading) return;
    setLoading(true);

    try {
      let q = query(
        collection(db, "users", user.id, "receivedApplications"),
        orderBy("appliedAt", "desc"),
        limit(PAGE_SIZE)
      );

      if (next && lastDoc) {
        q = query(
          collection(db, "users", user.id, "receivedApplications"),
          orderBy("appliedAt", "desc"),
          startAfter(lastDoc),
          limit(PAGE_SIZE)
        );
      }

      const snap = await getDocs(q);
      const page = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setHasMore(page.length === PAGE_SIZE);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);

      setApplications((prev) => (next ? [...prev, ...page] : page));
    } catch (err) {
      console.error("Error fetching applications:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /* ------------ ACTION HANDLERS ------------ */

  const handleAccept = async (app) => {
    try {
      const ref = doc(db, "users", user.id, "receivedApplications", app.id);
      await updateDoc(ref, { status: "accepted" });
      setApplications((prev) =>
        prev.map((a) => (a.id === app.id ? { ...a, status: "accepted" } : a))
      );
    } catch (err) {
      console.error("Error accepting application:", err);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal.app) return;
    try {
      const ref = doc(
        db,
        "users",
        user.id,
        "receivedApplications",
        rejectModal.app.id
      );
      await updateDoc(ref, {
        status: "rejected",
        rejectReason: rejectModal.reason || "No reason provided",
      });
      setApplications((prev) =>
        prev.map((a) =>
          a.id === rejectModal.app.id
            ? { ...a, status: "rejected", rejectReason: rejectModal.reason }
            : a
        )
      );
      setRejectModal({ open: false, app: null, reason: "" });
    } catch (err) {
      console.error("Error rejecting application:", err);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.app) return;
    try {
      const ref = doc(
        db,
        "users",
        user.id,
        "receivedApplications",
        deleteModal.app.id
      );
      await deleteDoc(ref);
      setApplications((prev) =>
        prev.filter((a) => a.id !== deleteModal.app.id)
      );
      setDeleteModal({ open: false, app: null });
    } catch (err) {
      console.error("Error deleting application:", err);
    }
  };

  return (
    <PageWrapper>
      <Container>
        <Title>Received Job Applications</Title>

        {applications.map((app) => (
          <AppCard key={app.id}>
            <JobInfo>
              <h3>{app.jobTitle}</h3>
              <p>
                <strong>Company:</strong> {app.companyName}
              </p>
              <p>
                <strong>Applicant Name:</strong> {app.appliedByName || "N/A"}
              </p>
              <p>
                <strong>Applicant Email:</strong> {app.appliedByEmail || "N/A"}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <StatusTag status={app.status}>{app.status}</StatusTag>
              </p>
              {app.rejectReason && (
                <p>
                  <strong>Reason:</strong> {app.rejectReason}
                </p>
              )}
              <p>
                <strong>Applied At:</strong>{" "}
                {app.appliedAt?.toDate
                  ? app.appliedAt.toDate().toLocaleString()
                  : "N/A"}
              </p>
            </JobInfo>
            <Actions>
              {app.resumeURL ? (
                <ResumeLink
                  href={app.resumeURL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Resume
                </ResumeLink>
              ) : (
                <span>No Resume</span>
              )}

              {app.status === "pending" && (
                <>
                  <ActionBtn onClick={() => handleAccept(app)} bg="#4ade80">
                    Accept
                  </ActionBtn>
                  <ActionBtn
                    onClick={() =>
                      setRejectModal({ open: true, app, reason: "" })
                    }
                    bg="#facc15"
                  >
                    Reject
                  </ActionBtn>
                </>
              )}
              <ActionBtn
                onClick={() => setDeleteModal({ open: true, app })}
                bg="#f87171"
              >
                Delete
              </ActionBtn>
            </Actions>
          </AppCard>
        ))}

        {!loading && applications.length === 0 && (
          <EmptyMsg>No applications found.</EmptyMsg>
        )}

        {hasMore && !loading && (
          <LoadMoreButton onClick={() => fetchApplications(true)}>
            Load More
          </LoadMoreButton>
        )}

        {loading && <LoadingMsg>Loading...</LoadingMsg>}

        {/* Reject Modal */}
        {rejectModal.open && (
          <ModalOverlay>
            <ModalBox>
              <h3>Reject Application</h3>
              <textarea
                placeholder="Enter reject reason..."
                value={rejectModal.reason}
                onChange={(e) =>
                  setRejectModal((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
              />
              <div>
                <ModalBtn onClick={handleRejectConfirm} bg="#f87171">
                  Confirm Reject
                </ModalBtn>
                <ModalBtn
                  onClick={() =>
                    setRejectModal({ open: false, app: null, reason: "" })
                  }
                >
                  Cancel
                </ModalBtn>
              </div>
            </ModalBox>
          </ModalOverlay>
        )}

        {/* Delete Modal */}
        {deleteModal.open && (
          <ModalOverlay>
            <ModalBox>
              <h3>Delete Application</h3>
              <p>Are you sure you want to delete this application?</p>
              <div>
                <ModalBtn onClick={handleDeleteConfirm} bg="#f87171">
                  Yes, Delete
                </ModalBtn>
                <ModalBtn
                  onClick={() => setDeleteModal({ open: false, app: null })}
                >
                  Cancel
                </ModalBtn>
              </div>
            </ModalBox>
          </ModalOverlay>
        )}
      </Container>
    </PageWrapper>
  );
};

export default JobApplicationsPage;

/* ---------------- Styled ---------------- */
/* ---------------- Improved Glassy Styled Components ---------------- */

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 40px 20px;
  background: radial-gradient(circle at top left, #0b0617, #000);
  display: flex;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const Title = styled.h2`
  text-align: center;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 700;
  margin-bottom: 28px;
`;

const AppCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  color: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(14px);
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 45px rgba(155, 92, 255, 0.25);
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const JobInfo = styled.div`
  font-size: 0.95rem;
  flex: 1;

  h3 {
    margin: 0 0 6px;
    font-size: 1.3rem;
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    margin: 6px 0;
    color: #ddd;
    line-height: 1.5;
  }
`;

const StatusTag = styled.span`
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 600;
  color: #fff;
  background: ${({ status }) =>
    status === "pending"
      ? "linear-gradient(45deg, #facc15, #eab308)"
      : status === "accepted"
      ? "linear-gradient(45deg, #4ade80, #22c55e)"
      : "linear-gradient(45deg, #f87171, #dc2626)"};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 150px;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`;

const ResumeLink = styled.a`
  padding: 10px 14px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  border-radius: 10px;
  text-decoration: none;
  font-size: 0.9rem;
  text-align: center;
  font-weight: 500;
  transition: all 0.25s ease;
  box-shadow: 0 5px 15px rgba(155, 92, 255, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(155, 92, 255, 0.35);
  }
`;

const ActionBtn = styled.button`
  padding: 10px 14px;
  background: ${({ bg }) =>
    bg ? bg : "linear-gradient(90deg, #9b5cff, #c59fff)"};
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.25s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

  &:hover {
    transform: translateY(-2px);
    opacity: 0.9;
  }
`;

const LoadMoreButton = styled(ActionBtn)`
  width: 100%;
  margin-top: 30px;
  font-size: 1rem;
  font-weight: 600;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
`;

const EmptyMsg = styled.p`
  text-align: center;
  color: #999;
  font-size: 1rem;
  padding: 20px 0;
`;

const LoadingMsg = styled.p`
  text-align: center;
  color: #fff;
  margin-top: 10px;
  font-size: 1rem;
`;

/* --------- Modal Styles --------- */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;
  backdrop-filter: blur(6px);
`;

const ModalBox = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 16px;
  padding: 28px;
  width: 400px;
  max-width: 95%;
  color: #fff;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  animation: fadeIn 0.3s ease;

  h3 {
    margin-bottom: 12px;
    font-size: 1.2rem;
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  textarea {
    width: 100%;
    min-height: 80px;
    margin: 12px 0;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid rgba(155, 92, 255, 0.25);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    font-size: 0.9rem;
    outline: none;
    resize: none;
    transition: all 0.25s;

    &:focus {
      border-color: #9b5cff;
      box-shadow: 0 0 10px rgba(155, 92, 255, 0.3);
    }
  }

  div {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 10px;
    flex-wrap: wrap;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalBtn = styled.button`
  padding: 10px 14px;
  background: ${({ bg }) =>
    bg ? bg : "linear-gradient(90deg, #9b5cff, #c59fff)"};
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.9;
  }
`;
